// v2.15.2 — Modale "Mes autres adresses" : flow paste-only.
// v2 premium (2026-06) — Refonte visuelle alignée sur l'identité V2 (vert profond + or).
// Flow :
//   1. User colle un texte contenant CIV-XXX-XXX-XXXXX
//   2. Décodage géohash → preview commune + pin mini-map
//   3. User saisit Nom + Description → POST /api/v3/addresses
// Pas de selects ville/commune visibles. Pas de clic carte.
// Lien discret "Saisir manuellement" pour ouvrir la modale legacy V3.

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import MiniMapPreview from "@/components/MiniMapPreview";
import AddressFormModalV3 from "@/components/AddressFormModalV3";
import "./DomicileGeolocModal.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CIV_PASTE_REGEX = /CIV-([A-Z]{2,4})-([A-Z]{2,4})-([A-Z0-9]+)/i;

export default function OtherAddressPasteModal({ open, userId, onClose, onCreated }) {
  const [rawText, setRawText] = useState("");
  const [decoded, setDecoded] = useState(null);
  const [decodeError, setDecodeError] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);

  const resetState = () => {
    setRawText(""); setDecoded(null); setDecodeError(null);
    setName(""); setDescription(""); setSubmitting(false);
  };

  const closeAll = () => { resetState(); onClose?.(); };

  const handleRawChange = async (e) => {
    const v = e.target.value;
    setRawText(v);
    setDecoded(null);
    setDecodeError(null);
    const m = String(v).match(CIV_PASTE_REGEX);
    if (!m) return;
    const cityCode = m[1].toUpperCase();
    const communeCode = m[2].toUpperCase();
    const suffix = m[3].toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-5);
    if (suffix.length !== 5) {
      setDecodeError("Code CIV non reconnu, vérifie le format (5 caractères après le 3e tiret)");
      return;
    }
    try {
      const r = await axios.post(`${API}/v3/addresses/decode-suffix`, { commune_code: communeCode, suffix });
      if (r.data?.ok) {
        setDecoded({
          address_civ: `CIV-${cityCode}-${communeCode}-${suffix}`,
          city_code: cityCode,
          commune_code: communeCode,
          suffix,
          lat: r.data.lat,
          lng: r.data.lng,
          precision_m: r.data.precision_m,
        });
      } else {
        setDecodeError(r.data?.error || "Code CIV non décodable");
      }
    } catch (e) {
      setDecodeError(e.response?.data?.error || "Code CIV non reconnu, vérifie le format");
    }
  };

  const submit = async () => {
    if (!decoded || !name.trim()) { toast.error("Code valide + nom requis"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/v3/addresses`, {
        user_id: userId,
        city_code: decoded.city_code,
        commune_code: decoded.commune_code,
        suffix: decoded.suffix,
        name: name.trim(),
        description: description.trim() || null,
        lat: decoded.lat,
        lng: decoded.lng,
        is_home: false,
      });
      if (r.data?.ok) {
        toast.success(`✅ Adresse ajoutée : ${r.data.address.address_civ}`);
        onCreated?.(r.data.address);
        resetState();
      } else {
        throw new Error(r.data?.reason || "create_failed");
      }
    } catch (e) {
      const reason = e.response?.data?.reason || e.message;
      if (reason === "name_taken") toast.error("Ce nom est déjà utilisé");
      else toast.error(reason);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open && !showLegacy) return null;

  if (showLegacy) {
    return (
      <AddressFormModalV3
        open={true}
        userId={userId}
        preferIsHome={false}
        onClose={() => { setShowLegacy(false); closeAll(); }}
        onCreated={(addr) => { setShowLegacy(false); onCreated?.(addr); }}
      />
    );
  }

  const submitDisabled = !decoded || !name.trim() || submitting;

  return (
    <div data-testid="other-paste-modal" className="dgm-overlay">
      <div className="dgm-modal">
        <button onClick={closeAll} data-testid="other-close-btn" className="dgm-close" aria-label="Fermer">✕</button>
        <div className="dgm-eyebrow">Bureau · Boutique · École · Famille</div>
        <h3 className="dgm-title">➕ Nouvelle <span className="dgm-aya">adresse</span></h3>

        <label className="dgm-label">📋 Colle ici le code adresse reçu</label>
        <textarea
          data-testid="paste-civ-textarea"
          value={rawText}
          onChange={handleRawChange}
          placeholder="Ex: CIV-ABJ-YOP-534CB"
          rows={3}
          className="dgm-input"
          style={{ fontFamily: "ui-monospace, Menlo, monospace", resize: "vertical", letterSpacing: 1 }}
        />

        {decodeError && (
          <div data-testid="paste-decode-error" className="dgm-banner" style={{ marginTop: 10, marginBottom: 0, background: "rgba(239,68,68,0.12)", borderColor: "rgba(239,68,68,0.5)", color: "#FECACA" }}>
            ⚠️ {decodeError}
          </div>
        )}

        {decoded && (
          <div data-testid="paste-decoded-block" style={{ marginTop: 12 }}>
            <div className="dgm-code" style={{ flexDirection: "column", gap: 4 }}>
              <span className="dgm-code-label">✅ Code décodé · {decoded.commune_code} · ±{decoded.precision_m} m</span>
              <span className="dgm-code-value">{decoded.address_civ}</span>
            </div>
            <div className="dgm-map">
              <MiniMapPreview lat={decoded.lat} lng={decoded.lng} onSet={() => {/* read-only */}} height={150} />
            </div>

            <label className="dgm-label">Nom *</label>
            <input
              type="text"
              data-testid="paste-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Bureau, Chez Maman"
              className="dgm-input"
              autoFocus
            />
            <label className="dgm-label">Description <span className="dgm-label-sub">(optionnel)</span></label>
            <input
              type="text"
              data-testid="paste-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Immeuble jaune"
              className="dgm-input"
            />
          </div>
        )}

        <div className="dgm-actions">
          <button onClick={closeAll} data-testid="paste-cancel-btn" className="dgm-btn dgm-btn-secondary">Annuler</button>
          <button
            onClick={submit}
            data-testid="paste-submit-btn"
            disabled={submitDisabled}
            className="dgm-btn dgm-btn-primary"
          >
            {submitting ? "…" : "✅ Ajouter cette adresse"}
          </button>
        </div>

        <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dashed rgba(255,255,255,0.12)", textAlign: "center" }}>
          <button
            type="button"
            data-testid="paste-fallback-manual-btn"
            onClick={() => setShowLegacy(true)}
            style={{ background: "transparent", border: "none", color: "rgba(236,245,238,0.5)", fontSize: 11.5, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}
          >
            Pas de code reçu ? Saisir manuellement (rare)
          </button>
        </div>
      </div>
    </div>
  );
}
